/**
 * DECISIONS 408's bots step (the owner: "probably need the bots to be able to conserve ammo / behave differently based on
 * their idea of supply"), with GameConfig.finiteSupply (core/supplyStock.ts): what a bot reads of its supply, and when it
 * conserves. Pure readers; with no stock (the switch off) every reader says "plenty" and nothing changes.
 * - **The side's share** (`sideSupplyShare`): its standing containers' magazines over the issues its tickets have still to
 *   draw (a mean issue each, core/supplyStock.ts issueMagazines). A side sized by 408's rule starts near its share (0.75 of
 *   the units and the tickets over the tickets alone); under 1 the stock can't arm every arrival to come. Null with no
 *   stock, or a side whose arrivals are unlimited (no ticket count to weigh it against).
 * - **The unit's share** (`longGunShare`): its long gun's rounds, in the gun and in reserve, over a full load.
 * - **Conserving** (`conserving`): the side's share under ACTION_PARAMS.supply.sideLow, or the unit's own under `unitLow` —
 *   fire discipline in ai/actionBot.ts (no area fire; aimed fire held past `holdBeyondPeople`).
 * - **Topping up** (`topUpFirst`): under a push (take, bound, flank) with nothing in sight, a member under `topUpShare` of
 *   its long gun's load, or short of bandages, re-arms at a stocked crate in its walk before it goes on — while its side
 *   isn't low.
 */
import type { GameState, PlayerState, SquadOrder } from '../types';
import { BANDAGE } from '../constants';
import { stockLeft, issueMagazines } from '../core/supplyStock';
import { weaponSpec, primaryOf, reserveLeft } from '../core/weapons';
import { bandagesLeft } from '../core/medical';
import type { ActionParams } from './botParamsAction';

/** Each state's side shares, read once a state: every bot of a tick reads the same state (the key is the state, every input). */
const shares = new WeakMap<object, Map<number, number | null>>();

/** The side's stock over the issues its tickets still draw; null with no stock or unlimited arrivals. */
export function sideSupplyShare(state: Pick<GameState, 'supplyStocks' | 'players' | 'modeState'>, team: number): number | null {
  if (!state.supplyStocks?.[team]) return null;
  let byTeam = shares.get(state);
  if (!byTeam) shares.set(state, (byTeam = new Map()));
  if (!byTeam.has(team)) byTeam.set(team, shareOf(state, team));
  return byTeam.get(team)!;
}

function shareOf(state: Pick<GameState, 'supplyStocks' | 'players' | 'modeState'>, team: number): number | null {
  const respawn = state.modeState.respawn;
  if (respawn?.unlimited?.includes(team)) return null;
  const units = state.players.filter(p => p.teamIndex === team);
  if (!units.length) return null;
  const issue = units.reduce((a, p) => a + issueMagazines(p), 0) / units.length;
  const tickets = respawn?.tickets[team] ?? 0;
  return stockLeft(state, team).magazines / Math.max(1, tickets * issue);
}

/** A unit's long gun's rounds, in the gun and in reserve, over a full load (1 for a gun with no limited spares). */
export function longGunShare(p: Pick<PlayerState, 'primary' | 'magazines' | 'reserves'>, ps: Parameters<typeof weaponSpec>[0], config: GameState['config']): number {
  const kind = primaryOf(p), spec = weaponSpec(ps, kind, config);
  if (spec.reserve === undefined) return 1;
  const cap = spec.magazine + spec.reserve;
  return cap > 0 ? ((p.magazines?.[kind] ?? spec.magazine) + reserveLeft(p, kind, spec)) / cap : 1;
}

/** The unit fires sparingly: its side's stock low for the arrivals to come, or its own long gun low. Never with no stock. */
export function conserving(state: Pick<GameState, 'supplyStocks' | 'players' | 'modeState' | 'config'>, p: PlayerState, ps: Parameters<typeof weaponSpec>[0], P: Pick<ActionParams, 'supply'>): boolean {
  if (!state.supplyStocks) return false;
  const side = sideSupplyShare(state, p.teamIndex);
  return (side !== null && side < P.supply.sideLow) || longGunShare(p, ps, state.config) < P.supply.unitLow;
}

/** A push's order: the squad goes at the enemy (take a place, bound, flank). */
export function pushOrder(o: SquadOrder | null): boolean {
  return !!o && (o.kind === 'take' || o.kind === 'bound' || o.kind === 'flank');
}

/**
 * Under a push, re-arm first: its long gun under `topUpShare` of a load, or short of bandages — while its side's stock isn't
 * low (a low side keeps its stock for the arrivals: a life ends before its kit does, so a top-up then is mostly stock lost
 * with the body; the Story A/B, sweeps/2026-11-01-408-bots-supply.md). Never with no stock.
 */
export function topUpFirst(state: Pick<GameState, 'supplyStocks' | 'players' | 'modeState' | 'config'>, p: PlayerState, o: SquadOrder | null, ps: Parameters<typeof weaponSpec>[0], P: Pick<ActionParams, 'supply'>): boolean {
  if (!state.supplyStocks || !pushOrder(o)) return false;
  const side = sideSupplyShare(state, p.teamIndex);
  if (side !== null && side < P.supply.sideLow) return false;
  return longGunShare(p, ps, state.config) < P.supply.topUpShare || bandagesLeft(p) < BANDAGE.carried;
}
